import React from 'react';
import styled from 'styled-components';
import useI18n from 'i18n';
import dayjs from 'dayjs';

import { useLogger, useCurrency, useSelector } from 'Hooks';

import Icon from 'Components/Icon';
import { ReactComponent as ClockIcon } from 'Assets/Icons/schedule.svg';
import { ReactComponent as EditIcon } from 'Assets/Icons/create.svg';
import { ReactComponent as StorefrontIcon } from 'Assets/Icons/storefront.svg';
import { lockBody } from 'utils/lockBody';
import getServiceTypeText from 'utils/getServiceTypeText';
import {
  Header,
  Info,
  Total,
  VAT,
  CartItems,
  PaymentButton,
  ModalContent,
  SignUpReminder,
  Tips,
} from './Components';
import { ModalType } from '.';
import { useShoppingCart, useShoppingCartApi } from 'Components/ShoppingCartUniverse';
import { useStorage } from 'Components/Storage';
import { useConfig } from 'Components/ConfigProvider';
import DateTimePickerDesktop from '../../Components/DateTimePickerDesktop';

const Wrapper = styled.div`
  height: var(--height);
  overflow: auto;
`;

const Content = styled.div<{ withOffer: boolean }>`
  padding-top: ${() => window.headerHeight}px;
  padding-bottom: 88px;
  background-color: #f8f8fb;
  min-height: calc(var(--height) - 68px);
  box-sizing: content-box;
  overflow: auto;
  animation: fade-in 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
  position: relative;
`;

const Inner = styled.div`
  @media (min-width: 992px) {
    margin: 0 auto;
    max-width: 960px;
  }
`;

interface Props {
  venueName: string;
  cartId: string | null;
  loading: boolean;
  isVenueOpen: {
    loading: boolean;
    open: boolean;
  };
  error: string | null;
  sessionData: any;
  onSetTime: (dateTime: string) => void;
  time: string;
  minimumPickupWaitTime: number;
  setError: React.Dispatch<React.SetStateAction<string>>;
  getSession: () => Promise<void>;
  updateDetails: () => Promise<void>;
  showModal: ModalType;
  setShowModal: React.Dispatch<React.SetStateAction<ModalType>>;
  setPayload: React.Dispatch<React.SetStateAction<string>>;
  checkAndUpdatePickUpTime: () => boolean;
  tips: number;
  setTips: React.Dispatch<React.SetStateAction<number>>;
  onInvoicePayment: () => void;
  checkCartItemAvailability: () => boolean;
}

const CartView: React.FC<Props> = ({
  venueName,
  loading,
  isVenueOpen,
  sessionData,
  onSetTime,
  time,
  minimumPickupWaitTime,
  showModal,
  setShowModal,
  error,
  setError,
  getSession,
  setPayload,
  updateDetails,
  checkAndUpdatePickUpTime,
  tips,
  onInvoicePayment,
  setTips,
  checkCartItemAvailability,
  cartId,
}) => {
  const config = useConfig();
  const logger = useLogger('checkout');
  const { i18n } = useI18n();
  const shoppingCart = useShoppingCart();
  const shoppingCartApi = useShoppingCartApi();
  const { formatAmount } = useCurrency();
  const [storageData] = useStorage();
  const isLoggedIn = !!storageData.authToken;
  const qrOrder = useSelector((state) => state.qrOrder);

  lockBody(false);
  const zeroCartValidation = Boolean(
    shoppingCart.total <= 0 &&
      shoppingCart.isReadyForCheckout &&
      shoppingCart.totalUnpaid === 0 &&
      shoppingCart.items.length > 0,
  );

  const paymentButtonHandler = () => {
    if (shoppingCart.pickupTime && shoppingCart.pickupTime !== 'ASAP') {
      const purchaseEnabled = checkAndUpdatePickUpTime();
      if (!purchaseEnabled) {
        return;
      }
    }
    const cartItemsUnavailable = checkCartItemAvailability();
    // If an item or variation is out of stock we exit the function
    if (cartItemsUnavailable) {
      return;
    }
    if (error) {
      setShowModal(ModalType.SessionError);
    }
    if (zeroCartValidation) {
      logger.info('Adding 0kr payment', { cartId: storageData.cartId, shoppingCart });
      shoppingCartApi.addClientSidePayment();
      setShowModal(ModalType.PaymentComplete);
    } else {
      setShowModal(ModalType.AdyenDropIn);
    }
  };

  const qrOrderEatHere = qrOrder && shoppingCart.serviceType === 'EAT_HERE';

  const showPickupTime =
    !config.DISABLE_PICKUP_TIME && !shoppingCart.deliveryTargetId && !qrOrderEatHere;

  return (
    <Wrapper>
      <Content withOffer={false}>
        <Header restaurant={venueName} serviceType={getServiceTypeText()} />
        <Inner>
          <CartItems />
          <Total price={formatAmount(shoppingCart.total)} discount={shoppingCart.totalDiscount} />
          <VAT />
          {showPickupTime &&
            (window.hasNativeWrapper ? (
              <Info
                title={i18n('Cart.ChangePickuptime')}
                text={
                  shoppingCart.pickupTime && shoppingCart.pickupTime !== 'ASAP'
                    ? dayjs(shoppingCart.pickupTime).format('DD/MM/YYYY[ ]HH:mm')
                    : i18n('Cart.Pickuptime.ASAP')
                }
                onClick={() => setShowModal(ModalType.DesiredTime)}
                icon={<Icon icon={<ClockIcon />} color={'#bdbdc0'} size={24} />}
              />
            ) : (
              <DateTimePickerDesktop
                title={i18n('Cart.ChangePickuptime')}
                text={
                  shoppingCart.pickupTime && shoppingCart.pickupTime !== 'ASAP'
                    ? dayjs(shoppingCart.pickupTime).format('DD/MM/YYYY[ ]HH:mm')
                    : i18n('Cart.Pickuptime.ASAP')
                }
                icon={<Icon icon={<ClockIcon />} color={'#bdbdc0'} size={24} />}
                time={time}
                onSubmit={onSetTime}
              />
            ))}
          {config.ENABLE_PROMOCODES && (
            <Info
              title={i18n('Cart.Modal.DiscountCode.Title')}
              text={shoppingCart.promoCode?.code ?? i18n('Cart.Modal.DiscountCode.Placeholder')}
              onClick={() => setShowModal(ModalType.Discount)}
              icon={<Icon icon={<StorefrontIcon />} color={'#bdbdc0'} size={24} />}
            />
          )}
          {!config.DISABLE_COMMENTS && (
            <Info
              title={i18n('Cart.AddComment')}
              text={shoppingCart.comment || i18n('Cart.NoComment')}
              onClick={() => setShowModal(ModalType.Comment)}
              icon={<Icon icon={<EditIcon />} color={'#bdbdc0'} size={24} />}
            />
          )}
          {config.ENABLE_TIPS && shoppingCart.total > 0 && <Tips tips={tips} setTips={setTips} />}
          {!shoppingCart.hasPendingPayment && (
            <PaymentButton
              buttonHandler={paymentButtonHandler}
              isVenueOpen={isVenueOpen}
              gettingSession={loading}
              tips={tips}
            />
          )}
          {!isLoggedIn && <SignUpReminder />}
        </Inner>
      </Content>
      <ModalContent
        sessionData={sessionData}
        time={time}
        onSetTime={onSetTime}
        setMode={(mode) => {
          setShowModal(mode);
        }}
        minimumPickupWaitTime={minimumPickupWaitTime}
        mode={showModal}
        error={error}
        getSession={getSession}
        setError={setError}
        setPayload={setPayload}
        updateDetails={updateDetails}
        loading={loading}
        onInvoicePayment={onInvoicePayment}
        cartId={cartId}
      />
    </Wrapper>
  );
};

export default CartView;
